import React, { useEffect, useState, useCallback } from 'react';
import {
  registerMobileWalletPicker,
  buildWalletBounceUrl,
  buildBounceTarget,
  type WalletBounceId,
} from '../utils/phantomMobile';
import { startPhantomSignIn, hasPersistentPhantomSession } from '../utils/phantomDeeplink';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../utils/analytics';

/**
 * Mobile wallet picker. Mounted once at the App root; phantomMobile.ts
 * registers a callback into us so any "user clicked something needing
 * a wallet" handler on mobile can pop this up by calling
 * `redirectToPhantomIfMobile(...)`.
 *
 * Per-wallet flow:
 *   - Phantom (deeplink): native phantom.app/ul/v1 protocol with two
 *     round-trips and reliable auto-return. UX cost: a manual
 *     "Continue with Phantom" tap between connect and signMessage.
 *   - Phantom (WC): WalletConnect v2 — single dialog in Phantom that
 *     bundles connect + sign, auto-return depends on Phantom honoring
 *     metadata.redirect (best-effort). Currently shipped alongside the
 *     deeplink for A/B comparison.
 *   - Jupiter: WalletConnect v2; Jupiter does NOT honor the redirect,
 *     so user must switch back to Safari themselves.
 *   - Solflare / Backpack: universal-link bounce into the wallet's
 *     in-app browser (works today, well-tested).
 */

interface BouncedWallet {
  kind: 'bounce';
  id: WalletBounceId;
  name: string;
  badgeBg: string;
  badgeText: string;
}
interface PhantomDeeplinkWallet {
  kind: 'phantom-deeplink';
  name: string;
  badgeBg: string;
  badgeText: string;
}
interface WalletConnectWallet {
  kind: 'wc';
  walletId: 'jupiter' | 'phantom';
  name: string;
  badgeBg: string;
  badgeText: string;
  /** Does the wallet auto-return to Safari after approval? Drives
   *  whether we show the "tap your Safari tab" prompt. */
  autoReturns: boolean;
}
type WalletEntry = BouncedWallet | PhantomDeeplinkWallet | WalletConnectWallet;

// Phantom (WC) was tested and removed: per Reown's wallet registry +
// Phantom's own docs, Phantom Mobile doesn't ship a WalletConnect
// handler — the `phantom://` scheme opens the app but there's no
// `wc?uri=` route, so the user lands on the Phantom homepage with no
// approval prompt. Native deeplink (utils/phantomDeeplink.ts) is the
// only working mobile-Phantom path; with persistent-session caching
// it's also one-tap on repeat sign-ins.
const WALLETS: WalletEntry[] = [
  { kind: 'phantom-deeplink',                                  name: 'Phantom',          badgeBg: 'bg-purple-500',  badgeText: 'P', },
  { kind: 'wc',     walletId: 'jupiter', autoReturns: false,   name: 'Jupiter',          badgeBg: 'bg-emerald-500', badgeText: 'J', },
  { kind: 'bounce', id: 'solflare',                            name: 'Solflare',         badgeBg: 'bg-orange-500',  badgeText: 'S', },
  { kind: 'bounce', id: 'backpack',                            name: 'Backpack',         badgeBg: 'bg-red-500',     badgeText: 'B', },
];

interface WCInFlight {
  walletLabel: string;
  badgeBg: string;
  badgeText: string;
  status: string;
  error: string | null;
  waitStartedAt: number | null;
  autoReturns: boolean;
}

const MobileWalletPicker: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<string | undefined>(undefined);
  const [wcInFlight, setWcInFlight] = useState<WCInFlight | null>(null);
  const [, setNow] = useState(Date.now());
  const [phantomFastPath, setPhantomFastPath] = useState(false);
  const { loginWithSignedMessage } = useAuth();

  // Live tick while waiting on a WC wallet — refreshes the
  // elapsed-seconds display so the picker doesn't look hung. iOS
  // suspends Safari while the user is in the wallet, so seeing the
  // counter resume on return tells them "yes, the page is alive".
  useEffect(() => {
    if (!wcInFlight?.waitStartedAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [wcInFlight?.waitStartedAt]);

  // Self-register so phantomMobile.ts can trigger us. On every open,
  // re-check whether a saved Phantom session is still on hand so the
  // "fast sign-in" hint reflects the current state.
  useEffect(() => {
    const fn = (a?: string) => {
      setAnchor(a);
      setWcInFlight(null);
      setPhantomFastPath(hasPersistentPhantomSession());
      setOpen(true);
    };
    registerMobileWalletPicker(fn);
    return () => registerMobileWalletPicker(null);
  }, []);

  // Esc to dismiss.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !wcInFlight?.status) setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, wcInFlight?.status]);

  const handleBounce = useCallback((id: WalletBounceId) => {
    trackEvent('mobile_wallet_picker_choice', { wallet: id, method: 'universal_link' });
    window.location.href = buildWalletBounceUrl(id, anchor);
  }, [anchor]);

  const handlePhantomDeeplink = useCallback(() => {
    trackEvent('mobile_wallet_picker_choice', { wallet: 'phantom', method: 'phantom_deeplink' });
    const returnUrl = anchor
      ? `${window.location.origin}${window.location.pathname}${window.location.search}#${anchor}`
      : buildBounceTarget();
    startPhantomSignIn({ returnUrl, anchor });
  }, [anchor]);

  const handleWalletConnect = useCallback(async (opt: WalletConnectWallet) => {
    setWcInFlight({
      walletLabel: opt.name,
      badgeBg: opt.badgeBg,
      badgeText: opt.badgeText,
      status: `Opening ${opt.name}…`,
      error: null,
      waitStartedAt: null,
      autoReturns: opt.autoReturns,
    });
    trackEvent('mobile_wallet_picker_choice', {
      wallet: opt.walletId,
      method: opt.walletId === 'phantom' ? 'phantom_walletconnect' : 'jupiter_walletconnect',
    });
    try {
      const message = `Sign in to deepnets.ai\nTimestamp: ${new Date().toISOString()}`;
      const messageBytes = new TextEncoder().encode(message);

      // Lazy-load the wallet-specific WC entry-point (which itself
      // lazy-loads the shared sign-client, ~133KB gz) so users who
      // never tap a WC wallet don't pay for it.
      const connect = opt.walletId === 'phantom'
        ? (await import('../utils/phantomMobileWC')).connectPhantomWCAndSignMessage
        : (await import('../utils/jupiterMobileSignIn')).connectJupiterAndSignMessage;

      const { publicKey, signature } = await connect(
        messageBytes,
        () => {
          setWcInFlight(prev => prev && {
            ...prev,
            status: opt.autoReturns
              ? `Approve in ${opt.name}…`
              : `Approve in ${opt.name}, then return to Safari`,
            waitStartedAt: Date.now(),
          });
        },
      );

      setWcInFlight(prev => prev && { ...prev, status: 'Signing you in…', waitStartedAt: null });
      const analyticsMethod = opt.walletId === 'phantom' ? 'phantom_walletconnect' : 'jupiter_walletconnect';
      await loginWithSignedMessage(publicKey, message, signature, analyticsMethod);
      setOpen(false);
      setWcInFlight(null);
    } catch (err: any) {
      const msg = err?.message || `${opt.name} sign-in failed`;
      let displayError: string;
      if (msg.toLowerCase().includes('rejected') || msg.toLowerCase().includes('cancel')) {
        displayError = `Cancelled in ${opt.name}`;
      } else if (msg.toLowerCase().includes('timed out')) {
        displayError = `Timed out — make sure ${opt.name} is installed and try again`;
      } else {
        displayError = msg;
      }
      setWcInFlight(prev => prev && { ...prev, status: '', error: displayError, waitStartedAt: null });
    }
  }, [loginWithSignedMessage]);

  if (!open) return null;

  const inFlight = !!wcInFlight?.status;
  const elapsedSec = wcInFlight?.waitStartedAt ? Math.floor((Date.now() - wcInFlight.waitStartedAt) / 1000) : 0;

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={() => { if (!inFlight) setOpen(false); }}
      />
      <div className="relative w-full sm:max-w-md bg-crypto-gray border border-gray-700 sm:rounded-xl rounded-t-xl shadow-2xl">
        <div className="p-5 border-b border-gray-800 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Open in your wallet</h2>
            <p className="text-gray-400 text-xs mt-1">
              Pick your wallet — we'll handle the rest.
            </p>
          </div>
          <button
            onClick={() => { if (!inFlight) setOpen(false); }}
            disabled={inFlight}
            className="text-gray-500 hover:text-white disabled:opacity-40 text-xl leading-none -mt-1"
          >
            ×
          </button>
        </div>

        {inFlight && wcInFlight ? (
          <div className="p-6 text-center">
            <div className="flex items-center justify-center mb-4">
              <div className={`w-9 h-9 rounded-full ${wcInFlight.badgeBg} flex items-center justify-center text-white font-bold mr-3`}>
                {wcInFlight.badgeText}
              </div>
              <div className="animate-spin w-5 h-5 border-2 border-crypto-accent border-t-transparent rounded-full" />
            </div>
            <p className="text-white font-medium mb-1">{wcInFlight.status}</p>
            {wcInFlight.waitStartedAt ? (
              <>
                {!wcInFlight.autoReturns && (
                  <p className="text-amber-400 text-xs font-medium">
                    {wcInFlight.walletLabel} doesn't switch back automatically — tap your Safari tab once you approve
                  </p>
                )}
                <p className="text-gray-500 text-[11px] mt-2">
                  Waiting · {elapsedSec}s
                </p>
                {elapsedSec > 15 && !wcInFlight.autoReturns && (
                  <p className="text-gray-600 text-[11px] mt-3 leading-snug">
                    iOS pauses Safari while you're in {wcInFlight.walletLabel}. The connection resumes within a few seconds of returning here.
                  </p>
                )}
              </>
            ) : (
              <p className="text-gray-500 text-xs">Hang tight…</p>
            )}
            {wcInFlight.error && (
              <p className="text-red-400 text-xs mt-3">{wcInFlight.error}</p>
            )}
          </div>
        ) : (
        <div className="p-3">
          {WALLETS.map(opt => {
            const onClick = opt.kind === 'wc' ? () => handleWalletConnect(opt)
                           : opt.kind === 'phantom-deeplink' ? handlePhantomDeeplink
                           : () => handleBounce((opt as BouncedWallet).id);
            const subtitle = opt.kind === 'wc'
              ? (opt.autoReturns
                  ? 'One-tap sign-in via WalletConnect'
                  : 'Sign in directly — no app switch back needed')
              : opt.kind === 'phantom-deeplink'
                ? (phantomFastPath
                    ? 'Saved — skip connect, just sign'
                    : 'Connect Phantom, then sign to log in')
                : null;
            // CTA reflects what tapping actually does:
            //   - bounce wallets: opens the wallet's in-app browser
            //   - phantom with saved session: jumps straight to signMessage
            //   - phantom without saved session: starts the connect step
            //   - WC wallets: starts the WalletConnect handshake
            const cta = opt.kind === 'bounce'
              ? 'Open →'
              : opt.kind === 'phantom-deeplink' && phantomFastPath
                ? 'Sign in →'
                : 'Connect →';
            return (
              <button
                key={opt.name}
                onClick={onClick}
                disabled={inFlight}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-crypto-dark disabled:opacity-50 transition-colors text-left"
              >
                <span className={`flex items-center justify-center w-9 h-9 rounded-full font-bold text-white ${opt.badgeBg}`}>
                  {opt.badgeText}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="text-white font-medium block">{opt.name}</span>
                  {subtitle && (
                    <span className="text-xs block truncate text-gray-500">
                      {subtitle}
                    </span>
                  )}
                </span>
                <span className="text-crypto-accent text-xs whitespace-nowrap">{cta}</span>
              </button>
            );
          })}
        </div>

        )}

        <div className="p-3 pt-0">
          <button
            onClick={() => { if (!inFlight) setOpen(false); }}
            disabled={inFlight}
            className="w-full text-center text-xs text-gray-500 hover:text-gray-300 disabled:opacity-40 py-2"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileWalletPicker;
